import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';

const initialState = {
  currentDate: new Date().toISOString(),
  events: {},
  loading: true,
  error: null,
  selectedDate: null,
  downloadStartDate: '',
  downloadEndDate: '',
  isDownloading: false,
  downloadMode: 'range',
  specificDownloadDates: [],
};

export const fetchHearingEvents = createAsyncThunk(
  'hearingCalendar/fetchHearingEvents',
  async ({ year, month }, { rejectWithValue }) => {
    try {
      const firstDayOfMonth = new Date(year, month, 1);
      const lastDayOfMonth = new Date(year, month + 1, 0);

      const params = {
        startDate: firstDayOfMonth.toISOString().split('T')[0],
        endDate: lastDayOfMonth.toISOString().split('T')[0],
      };

      const response = await api.get('/records/hearing-events', { params });
      const eventsByDate = (response.data || []).reduce((acc, event) => {
        const eventDate = new Date(event.hearingDate);
        const date = new Date(
          Date.UTC(
            eventDate.getUTCFullYear(),
            eventDate.getUTCMonth(),
            eventDate.getUTCDate()
          )
        )
          .toISOString()
          .split('T')[0];
        if (!acc[date]) acc[date] = [];
        acc[date].push(event);
        return acc;
      }, {});

      return eventsByDate;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to fetch hearing events.';
      return rejectWithValue({ message });
    }
  }
);

const hearingCalendarSlice = createSlice({
  name: 'hearingCalendar',
  initialState,
  reducers: {
    setCurrentDate(state, action) {
      state.currentDate = action.payload;
    },
    setSelectedDate(state, action) {
      state.selectedDate = action.payload;
    },
    setDownloadStartDate(state, action) {
      state.downloadStartDate = action.payload;
    },
    setDownloadEndDate(state, action) {
      state.downloadEndDate = action.payload;
    },
    setIsDownloading(state, action) {
      state.isDownloading = action.payload;
    },
    setDownloadMode(state, action) {
      state.downloadMode = action.payload;
      if (action.payload === 'range') {
        state.specificDownloadDates = [];
      }
    },
    setSpecificDownloadDates(state, action) {
      state.specificDownloadDates = action.payload;
    },
    toggleSpecificDownloadDate(state, action) {
      const dateStr = action.payload;
      state.specificDownloadDates = state.specificDownloadDates.includes(dateStr)
        ? state.specificDownloadDates.filter((d) => d !== dateStr)
        : [...state.specificDownloadDates, dateStr];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHearingEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchHearingEvents.fulfilled, (state, action) => {
        state.events = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchHearingEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch hearing events.';
      });
  },
});

export const {
  setCurrentDate,
  setSelectedDate,
  setDownloadStartDate,
  setDownloadEndDate,
  setIsDownloading,
  setDownloadMode,
  setSpecificDownloadDates,
  toggleSpecificDownloadDate,
} = hearingCalendarSlice.actions;

export const selectHearingCalendar = (state) => state.hearingCalendar;

export default hearingCalendarSlice.reducer;
